export interface CancellationTier {
  hours_before: number;
  percent: number;
}

export interface CancellationFeeResult {
  fee: number;
  percent: number;
  hoursLeft: number;
  tierLabel: string;
}

// ─── Internal: shared sort + tier-pick logic ───────────────────────────────
// EXTRAHERAD i Lager 2.2 så att calculateCancellationFee (spelar-flow) +
// resolveTierRefundFromBooking (admin-flow) återanvänder EXAKT samma
// algoritm. Ingen parallell DSL — single-source-of-truth för tier-resolution.
function pickMatchingTier(
  tiers: CancellationTier[],
  hoursLeft: number,
): CancellationTier | null {
  if (tiers.length === 0) return null;
  // Sort DESC by hours_before
  const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
  // Pick first tier där hoursLeft >= hours_before; fallback till lägsta tier
  let matched = sorted[sorted.length - 1];
  for (const tier of sorted) {
    if (hoursLeft >= tier.hours_before) {
      matched = tier;
      break;
    }
  }
  return matched;
}

function computeHoursLeft(startTime: Date, now: Date): number {
  // UTC-millis baserad → DST-immune; tidszon-säker
  return Math.max(0, (startTime.getTime() - now.getTime()) / (1000 * 60 * 60));
}

/**
 * Calculate cancellation fee based on tiered rules.
 * Tiers are sorted highest hours_before first.
 * Logic: find first tier where hoursLeft >= hours_before → that tier's percent applies.
 * If hoursLeft is less than all tiers, the last tier (lowest hours_before) applies.
 */
export function calculateCancellationFee(
  bookingPrice: number,
  startTime: Date,
  tiers: CancellationTier[],
  now: Date = new Date()
): CancellationFeeResult {
  const hoursLeft = computeHoursLeft(startTime, now);
  const matchedTier = pickMatchingTier(tiers, hoursLeft);

  if (matchedTier === null) {
    return { fee: 0, percent: 0, hoursLeft, tierLabel: "Gratis avbokning" };
  }

  const fee = Math.round(bookingPrice * matchedTier.percent / 100);
  const tierLabel = matchedTier.percent === 0
    ? "Gratis avbokning"
    : `${matchedTier.percent}% avgift`;

  return { fee, percent: matchedTier.percent, hoursLeft, tierLabel };
}

// ─── Lager 2.2 — admin refund-tier-resolver ────────────────────────────────
//
// Pluggar in P1 (Alt A — `organizations.settings.cancellation.tiers` single-
// source) i admin-refund-flowens `per_rules`-mode (`resolveRefundCancelPlan`).
// Wrappar `pickMatchingTier` (samma logik som spelar-flowens
// `calculateCancellationFee`) och konverterar fee-% → refund-%.
//
// Used by: `BookingRefundCancelDialog` när admin väljer "Återbetala enligt
// regler". Resultatets `refundPercent` matas in i resolveRefundCancelPlan
// som `rulesRefundPercent` → modulen hanterar auto-degrade (0% → cancel,
// 100% → full_override) + öre-exakt partial refund i [0, 100].

export interface TierRefundResult {
  /** Refund-% (0-100). = 100 − matchedTier.percent. */
  refundPercent: number;
  /** Matchande tier som valdes (för UI-display + audit-trail). */
  matchedTier: CancellationTier;
  /** UTC-baserad timdiff start − now (DST-immune). */
  hoursLeft: number;
  /** Human-readable tier-beskrivning för UI ("50% återbetalning..."). */
  tierLabel: string;
  /** Fee-% (matchedTier.percent), för fullständig audit-spårbarhet. */
  feePercent: number;
}

/**
 * Resolve refund-% från tier-regler för en specifik bokning.
 * PURE — no Supabase, no React, default `now = new Date()` parametriserbar
 * för tester. Tom tiers-array → returnerar full refund (gratis) — admin
 * får alltid en plan att exekvera, även när inga regler finns konfigurerade.
 */
export function resolveTierRefundFromBooking(args: {
  startTime: Date;
  tiers: CancellationTier[];
  now?: Date;
}): TierRefundResult {
  const { startTime, tiers, now = new Date() } = args;
  const hoursLeft = computeHoursLeft(startTime, now);

  if (tiers.length === 0) {
    // Inga regler → full återbetalning (admin har discretion via
    // BookingDetailPanel D2-knapp "Återbetala enligt regler"; auto-upgrade
    // till full_override i resolveRefundCancelPlan)
    return {
      refundPercent: 100,
      matchedTier: { hours_before: 0, percent: 0 },
      hoursLeft,
      tierLabel: "Full återbetalning (inga regler konfigurerade)",
      feePercent: 0,
    };
  }

  const matchedTier = pickMatchingTier(tiers, hoursLeft)!; // tiers.length>0 → non-null
  const refundPercent = 100 - matchedTier.percent;

  let tierLabel: string;
  if (matchedTier.percent === 0) {
    tierLabel = "Full återbetalning (ingen avgift)";
  } else if (matchedTier.percent === 100) {
    tierLabel = "Ingen återbetalning (100% avgift)";
  } else {
    tierLabel = `${refundPercent}% återbetalning (${matchedTier.percent}% avgift)`;
  }

  return {
    refundPercent,
    matchedTier,
    hoursLeft,
    tierLabel,
    feePercent: matchedTier.percent,
  };
}

/**
 * Convert legacy cancellation settings to tiers format.
 */
export function legacyToTiers(
  cancellationHours: number | undefined,
  lateCancelFee: number | undefined
): CancellationTier[] {
  const hours = cancellationHours ?? 24;
  const hasLateFee = (lateCancelFee ?? 0) > 0;
  return [
    { hours_before: hours, percent: 0 },
    { hours_before: 0, percent: hasLateFee ? 100 : 0 },
  ];
}

/**
 * Generate preview lines for a set of tiers at a given example price.
 */
export function generateTierPreview(
  tiers: CancellationTier[],
  examplePrice: number
): { label: string; fee: number; percent: number }[] {
  const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
  return sorted.map((tier, i) => {
    const _nextTier = sorted[i + 1];
    const fee = Math.round(examplePrice * tier.percent / 100);
    let label: string;
    if (i === 0) {
      label = `Mer än ${tier.hours_before}h före`;
    } else if (i === sorted.length - 1) {
      label = `Under ${sorted[i - 1]?.hours_before ?? "?"}h före`;
    } else {
      label = `${tier.hours_before}–${sorted[i - 1].hours_before}h före`;
    }
    return { label, fee, percent: tier.percent };
  });
}
